import { FunctionComponent } from 'react';
import {
  EuiCollapsibleNav,
  EuiCollapsibleNavGroup,
  EuiListGroup,
  type EuiListGroupItemProps,
  EuiHeaderSectionItemButton,
  EuiIcon,
  EuiButton,
} from '@elastic/eui';
import { useRouter } from 'next/router';
import { useUiChrome } from './useUiChrome';
import { useHasRole } from '@lib/auth/useHasRole';
import { useFeature } from '@lib/feature-toggles/useFeature';
import { FeatureFlag } from '@lib/feature-toggles/flags';
import { Roles } from '@lib/domain/auth';
import { TbSwitchHorizontal } from 'react-icons/tb';

/**
 * Global burger navigation on `EuiCollapsibleNav`. On l/xl it is docked-push
 * (a fixed rail that shifts content) and stays open; on xs/s/m it is an overlay
 * toggled by the ☰ (which lives in the secondary Toolbar). `isDocked` comes
 * from `useUiChrome`, which mount-gates the breakpoint so the docked rail
 * appears after hydration (no SSR mismatch). The two-drawer rule closes the
 * filter drawer when the nav opens on overlay breakpoints.
 */
export const BurgerNav: FunctionComponent = () => {
  const router = useRouter();
  const { isNavOpen, openNav, closeNav, isDocked } = useUiChrome();

  // Gating (static items are fine for the slice).
  const isAdmin = useHasRole(Roles.Admin);
  const isApprover = useHasRole(Roles.Approver);
  const hasActivities = useFeature(FeatureFlag.ActivitiesOverview);

  const go = (href: string) => {
    router.push(href);
    // On desktop the rail is docked/persistent; only close it on overlay.
    if (!isDocked) closeNav();
  };

  const navItems: EuiListGroupItemProps[] = [
    {
      label: 'My Dashboard',
      iconType: 'dashboardApp',
      onClick: () => go('/'),
      isActive: router.pathname === '/',
    },
    // `/campaigns/*` is Admin-only in middleware — don't show the link to
    // non-admins (they'd just bounce to /403).
    ...(isAdmin
      ? [
          {
            label: 'Campaigns',
            iconType: 'visBarVertical',
            onClick: () => go('/campaigns'),
            isActive: router.pathname.startsWith('/campaigns'),
          } as EuiListGroupItemProps,
        ]
      : []),
    // Activity surface gated; activity-side data is out of scope but the nav
    // entry point is part of the chrome.
    ...(isAdmin && hasActivities
      ? [
          {
            label: 'Activities',
            iconType: 'calendar',
            onClick: () => go('/activities'),
            isActive: router.pathname.startsWith('/activities'),
          } as EuiListGroupItemProps,
        ]
      : []),
    // Approvals is gated on the Approver role *independently of Admin* — the
    // `/approvals` queue is self-contained and middleware gates it on Approver
    // alone, so a pure approver (no Admin) must be able to reach it too.
    ...(isApprover
      ? [
          {
            label: 'Approvals',
            iconType: 'flag',
            onClick: () => go('/approvals'),
            isActive: router.pathname.startsWith('/approvals'),
          } as EuiListGroupItemProps,
        ]
      : []),
    {
      label: 'Support',
      iconType: 'help',
      onClick: () => go('/support'),
      isActive: router.pathname.startsWith('/support'),
    },
  ];

  const toggleButton = (
    <EuiHeaderSectionItemButton
      size="s"
      aria-label="Toggle main navigation"
      onClick={() => (isNavOpen ? closeNav() : openNav())}>
      <EuiIcon type="menu" size="m" />
    </EuiHeaderSectionItemButton>
  );

  return (
    <EuiCollapsibleNav
      aria-label="Main navigation"
      isOpen={isDocked || isNavOpen}
      isDocked={isDocked}
      dockedBreakpoint="l"
      button={toggleButton}
      onClose={closeNav}
      // No-shadow / no-border to match our panel convention; drop the docked
      // rail's right divider + shadow.
      css={{
        borderInlineEnd: 'none',
        boxShadow: 'none',
      }}>
      <EuiCollapsibleNavGroup title="Navigate" iconType="grid">
        <EuiListGroup
          listItems={navItems}
          maxWidth="none"
          color="subdued"
          gutterSize="none"
          size="s"
        />
      </EuiCollapsibleNavGroup>
      <div style={{ padding: 12 }}>
        <EuiButton
          onClick={() => router.push('https://campaign-manager.voteda.org')}
          iconSide="right"
          iconType={TbSwitchHorizontal}
          size="s"
          fullWidth>
          <strong>Switch to Old Version</strong>
        </EuiButton>
      </div>
    </EuiCollapsibleNav>
  );
};

export default BurgerNav;
